/**
 * C5SaveSlots — save slots in the "Indigo & Ink" language, drawn in Phaser.
 *
 * Doctrine: docs/design/ui-c5-indigo-ink.md ("Save slots | Wax seals with slot
 * glyph"). Unlike the other Layer-1 surfaces this one is NOT DOM: the title
 * flow is a Phaser scene with camera fades, a painted backdrop and its own
 * depth ordering, and a DOM panel would have to fight all three. The doctrine's
 * palette note covers exactly this case — Phaser-drawn surfaces map to the
 * shared C5_* constants in ui/palette.ts, which mirror c5-tokens.css.
 *
 * Grammar applied here:
 *   - an occupied slot is a wax seal stamped with its number, and the save's
 *     details ride on a paper tag beside it — paper is earned, so only a slot
 *     someone actually played gets any;
 *   - an empty slot is a faint dashed ring with dim console text, no paper;
 *   - hover lifts the card a couple of pixels over 180ms (doctrine rule 4).
 *
 * TitleScene owns all behaviour; this module only draws and forwards clicks.
 */

import * as Phaser from 'phaser';
import {
  C5_BAND,
  C5_BAND_LINE,
  C5_HANKO,
  C5_INK,
  C5_INK_DIM,
  C5_PAPER,
  C5_PAPER_DK,
  C5_PAPER_HI,
  C5_SOCKET,
  C5_SOCKET_RIM,
  C5_TEXT,
  PAL_BLACK,
  hexToCSS,
} from '../palette';
import { getString } from '../locale';
import { formatLastPlayed, formatPlaytime, type SaveSlotData } from '../SaveSlotPersistence';

/** Wax seal radius, in px at the title screen's design scale. */
const SEAL_RADIUS = 22;
/** Dashed-ring segment count for an empty slot. */
const RING_SEGMENTS = 14;
/** Hover lift, and its duration (doctrine rule 4: nothing snappier than 250ms). */
const HOVER_LIFT = 2;
const HOVER_MS = 180;
/** Paper tag sits a hair off-square, the way a glued tag would. */
const TAG_ANGLE = -0.5;
/** Opacity of the console plate under each card. */
const PLATE_ALPHA = 0.62;

export interface C5SlotCardOptions {
  index: number;
  data: SaveSlotData | null;
  /** Card centre, in scene coordinates. */
  cx: number;
  cy: number;
  width: number;
  height: number;
  /** Title-screen display face, so slots match the rest of the scene. */
  font: string;
  onSelect: () => void;
  onDelete: () => void;
}

/**
 * Draw one save slot into `parent`. Returns the card's own container so the
 * caller can tween or destroy it as a unit.
 */
export function buildC5SlotCard(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  opts: C5SlotCardOptions,
): Phaser.GameObjects.Container {
  const { index, data, width, height, font } = opts;
  const card = scene.add.container(opts.cx, opts.cy);
  const left = -width / 2;

  // A quiet console plate under each card. The title screen's painting is the
  // brightest surface in the game; without it neither the seal nor the dim
  // empty-slot text clears the readability bar (GDD Area 8).
  const plate = scene.add.rectangle(0, 0, width, height, C5_BAND, PLATE_ALPHA);
  plate.setStrokeStyle(1, C5_BAND_LINE);
  card.add(plate);

  // ── the seal (or the absence of one) ────────────────────────────────────
  const sealX = left + SEAL_RADIUS + 18;
  if (data) {
    card.add(buildWaxSeal(scene, sealX, 0, index, font));
  } else {
    card.add(buildEmptyRing(scene, sealX, 0));
  }

  if (data) {
    // ── paper tag: name, vocation, and the hours already spent ────────────
    const tagW = width - (SEAL_RADIUS * 2 + 44);
    const tagH = height - 18;
    const tag = scene.add.container(sealX + SEAL_RADIUS + 16 + tagW / 2, 0);
    tag.setAngle(TAG_ANGLE);

    const body = scene.add.rectangle(0, 0, tagW, tagH, C5_PAPER);
    const lit = scene.add.rectangle(0, -tagH / 2 + 2, tagW, 4, C5_PAPER_HI);
    const deckle = scene.add.rectangle(0, tagH / 2 - 1.5, tagW, 3, C5_PAPER_DK);
    tag.add([body, lit, deckle]);

    const padX = tagW / 2 - 14;
    const name = scene.add
      .text(-padX, -tagH / 2 + 12, data.characterName, {
        fontFamily: font,
        fontSize: '20px',
        color: hexToCSS(C5_INK),
      })
      .setOrigin(0, 0);
    const vocation = scene.add
      .text(-padX, -tagH / 2 + 38, data.vocation, {
        fontFamily: font,
        fontSize: '13px',
        color: hexToCSS(C5_INK_DIM),
      })
      .setOrigin(0, 0);
    const meta = scene.add
      .text(
        padX,
        -tagH / 2 + 12,
        `${formatPlaytime(data.playtimeSeconds)}\n${formatLastPlayed(data.lastPlayedISO)}`,
        {
          fontFamily: font,
          fontSize: '13px',
          color: hexToCSS(C5_INK_DIM),
          align: 'right',
        },
      )
      .setOrigin(1, 0);
    tag.add([name, vocation, meta]);
    card.add(tag);

    // Delete mark, in the hanko red so it reads as a stamp being struck out.
    const delMark = scene.add
      .text(width / 2 - 15, -height / 2 + 15, getString('title.slots.delete_mark'), {
        fontFamily: font,
        fontSize: '14px',
        color: hexToCSS(C5_HANKO),
      })
      .setOrigin(0.5, 0.5);
    const delHit = scene.add
      .rectangle(width / 2 - 15, -height / 2 + 15, 26, 26, PAL_BLACK, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    delHit.on('pointerover', () => delMark.setAlpha(1));
    delHit.on('pointerout', () => delMark.setAlpha(0.75));
    delHit.on(
      'pointerdown',
      (
        _p: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        opts.onDelete();
      },
    );
    delMark.setAlpha(0.75);
    card.add([delMark, delHit]);
  } else {
    // No paper for a slot nobody has written on yet — dim console text only.
    const empty = scene.add
      .text(sealX + SEAL_RADIUS + 20, 0, getString('title.slots.empty'), {
        fontFamily: font,
        fontSize: '18px',
        fontStyle: 'italic',
        color: hexToCSS(C5_TEXT),
      })
      .setOrigin(0, 0.5);
    card.add(empty);
  }

  // ── whole-card hit target + gentle hover lift ───────────────────────────
  const hit = scene.add
    .rectangle(0, 0, width, height, PAL_BLACK, 0)
    .setOrigin(0.5, 0.5)
    .setInteractive({ useHandCursor: true });
  hit.on('pointerover', () => liftTo(scene, card, opts.cy - HOVER_LIFT));
  hit.on('pointerout', () => liftTo(scene, card, opts.cy));
  hit.on('pointerdown', () => opts.onSelect());
  card.add(hit);

  parent.add(card);
  return card;
}

function liftTo(scene: Phaser.Scene, card: Phaser.GameObjects.Container, y: number): void {
  scene.tweens.add({ targets: card, y, duration: HOVER_MS, ease: 'Sine.easeOut' });
}

/** A struck wax seal carrying the slot's number. */
function buildWaxSeal(
  scene: Phaser.Scene,
  x: number,
  y: number,
  index: number,
  font: string,
): Phaser.GameObjects.Container {
  const seal = scene.add.container(x, y);
  const well = scene.add.circle(0, 0, SEAL_RADIUS + 4, C5_SOCKET);
  well.setStrokeStyle(1.5, C5_SOCKET_RIM);
  const wax = scene.add.circle(0, 0, SEAL_RADIUS, C5_HANKO);
  wax.setStrokeStyle(1, PAL_BLACK, 0.45);
  // Off-centre sheen: where the stamp caught the light as it was pressed.
  const sheen = scene.add.circle(-SEAL_RADIUS * 0.28, -SEAL_RADIUS * 0.3, SEAL_RADIUS * 0.55, C5_PAPER_HI);
  sheen.setAlpha(0.14);
  // The slot glyph is the numeral itself (doctrine: "wax seals with slot
  // glyph"), so there is no string to localize here.
  const glyph = scene.add
    .text(0, 0, String(index + 1), {
      fontFamily: font,
      fontSize: '16px',
      color: hexToCSS(C5_PAPER_HI),
    })
    .setOrigin(0.5, 0.5);
  seal.add([well, wax, sheen, glyph]);
  seal.setAngle(-3);
  return seal;
}

/** A slot with nothing in it: a faint dashed ring. */
function buildEmptyRing(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.lineStyle(1.5, C5_SOCKET_RIM, 0.5);
  const step = (Math.PI * 2) / RING_SEGMENTS;
  for (let i = 0; i < RING_SEGMENTS; i++) {
    const start = i * step;
    g.beginPath();
    g.arc(0, 0, SEAL_RADIUS + 4, start, start + step * 0.55, false);
    g.strokePath();
  }
  return g;
}
